/**
 * RightPanel — unified tabbed right sidebar.
 *
 * Two visual states:
 *   - Expanded: full panel with tab bar, content area, and close button
 *   - Collapsed: narrow vertical strip with rotated tab labels (see CollapsedRightPanel)
 *
 * Tabs:
 *   - "Plan"      — the agent's live TODO checklist (TodoWrite-driven)
 *   - "Shortlist" — the candidate shortlist table + version history
 *   - "Files"     — agent-generated artifacts AND user uploads / tool-referenced
 *                   files, split into two stacked sections inside the tab
 *
 * Tabs with no content are hidden. The Files tab counts ``artifacts +
 * other files`` so the tab badge reflects everything inside, not just the
 * Artifacts subsection.
 */

import { useMemo } from "react";
import { useChatStore } from "@/stores/chatStore";
import { useShortlistStore } from "@/stores/shortlistStore";
import { ContextPanel } from "./ContextPanel";
import { SubagentsPanel } from "./SubagentsPanel";
import { ShortlistTable } from "./ShortlistTable";
import { VersionHistory } from "./VersionHistory";
import { ArtifactsPanel } from "./ArtifactsPanel";
import { PlanPanel } from "@/components/chat/PlanPanel";
import { extractOtherFiles, basenameOf } from "./otherFiles";

type TabKey = "plan" | "shortlist" | "files" | "context" | "subagents";

/** Build the list of tabs that have content. Exported so the Header can
 *  decide whether to show its mobile "open panel" trigger (the collapsed
 *  edge strip is desktop-only). */
export function useVisibleTabs() {
  const artifacts = useChatStore((s) => s.artifacts);
  const messages = useChatStore((s) => s.messages);
  const todoCount = useChatStore((s) => s.todos.length);
  const liveData = useShortlistStore((s) => s.liveData);
  const activeShortlist = useShortlistStore((s) => s.activeShortlist);
  const contextBreakdown = useChatStore((s) => s.contextBreakdown);
  const subagentRuns = useChatStore((s) => s.subagentRuns);
  const shortlistData = liveData ?? activeShortlist?.data ?? null;

  // Mirror ArtifactsPanel's dedup so the Files tab badge matches the number of
  // cards actually rendered inside it. Key on the artifact's *path basename*
  // (e.g. ``voice-infra-decision.html``) — NOT its display ``filename`` (e.g.
  // "Voice-Agent Infrastructure Brief …"), which never matches the basename an
  // upload / Write tool_use carried. Keying on ``filename`` double-counted an
  // uploaded file the agent later edited in place and saved as an artifact: the
  // panel collapsed the two to one card, but the badge still counted both.
  const otherFilesCount = useMemo(() => {
    const artifactBasenames = new Set(
      artifacts.map((a) => (a.file_path ? basenameOf(a.file_path) : a.filename)),
    );
    return extractOtherFiles(messages, artifactBasenames).length;
  }, [messages, artifacts]);

  const fileCount = artifacts.length + otherFilesCount;

  // Plan first — it's the agent's live state and the most time-sensitive
  // thing to glance at while a run is in flight.
  const tabs: Array<{ key: TabKey; label: string; count?: number }> = [];
  if (todoCount > 0) tabs.push({ key: "plan", label: "Plan", count: todoCount });
  if (shortlistData) tabs.push({ key: "shortlist", label: "Shortlist", count: shortlistData.evaluations?.length });
  if (fileCount > 0) tabs.push({ key: "files", label: "Files", count: fileCount });
  // Sub-agents (ADR-0019). Sits beside Plan because it is also live agent
  // state, and the badge counts only RUNNING ones: a count of everything the
  // chat ever delegated would be a permanent number nobody needs to act on.
  // The tab itself appears whenever there is any history, so a user can go
  // find out what happened after the fact — which is the case that was
  // impossible before.
  if (subagentRuns.length > 0) {
    const live = subagentRuns.filter((r) => r.status === "running").length;
    tabs.push({
      key: "subagents",
      label: "Sub-agents",
      count: live > 0 ? live : undefined,
    });
  }
  // Context last — reference material you consult, not something you watch.
  // Only once a turn has actually produced a reading.
  if (contextBreakdown) tabs.push({ key: "context", label: "Context" });

  return { tabs, shortlistData, activeShortlist };
}

/** Expanded right panel with tabbed content. */
export function RightPanel() {
  const tab = useChatStore((s) => s.rightPanelTab);
  const setTab = useChatStore((s) => s.setRightPanelTab);
  const closePanel = useChatStore((s) => s.closeRightPanel);

  const { tabs, shortlistData, activeShortlist } = useVisibleTabs();

  // If current tab has no content, switch to one that does
  const activeTab = tabs.find((t) => t.key === tab) ? tab : tabs[0]?.key ?? "files";

  return (
    <div className="h-full flex flex-col bg-surface">
      {/* Header: tabs + close */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-space-30 shrink-0">
        {tabs.length > 1 ? (
          <div className="flex gap-1">
            {tabs.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150
                  ${activeTab === t.key
                    ? "bg-jove-30 text-jove"
                    : "text-space-40 hover:text-space hover:bg-space-20"
                  }`}
              >
                {t.label}
                {t.count != null && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold
                    ${activeTab === t.key ? "bg-primary text-on-primary" : "bg-space-20 text-space-40"}`}>
                    {t.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        ) : (
          <h3 className="text-sm font-bold text-space px-1">
            {tabs[0]?.label ?? "Panel"}
          </h3>
        )}

        <button
          onClick={closePanel}
          className="p-1.5 text-space-40 hover:text-space hover:bg-space-20 rounded-lg transition-all duration-150"
          title="Close panel"
        >
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto scrollbar-on-hover">
        {activeTab === "plan" && (
          <PlanPanel />
        )}
        {activeTab === "shortlist" && shortlistData && (
          <ShortlistTable data={shortlistData} />
        )}
        {activeTab === "subagents" && <SubagentsPanel />}
        {activeTab === "context" && <ContextPanel />}
        {activeTab === "files" && (
          <ArtifactFilesList />
        )}
      </div>

      {/* Version history footer (shortlist tab only) */}
      {activeTab === "shortlist" && activeShortlist && activeShortlist.version > 1 && (
        <VersionHistory
          shortlistId={activeShortlist.id}
          currentVersion={activeShortlist.version}
        />
      )}
    </div>
  );
}

/** Collapsed vertical tab strip shown along the right edge. */
export function CollapsedRightPanel() {
  const openRightPanel = useChatStore((s) => s.openRightPanel);
  const { tabs } = useVisibleTabs();

  return (
    // Desktop-only: on mobile the panel is reached via the Header's "open
    // panel" button (this narrow rotated-label strip would just eat width
    // from an already-cramped phone column).
    <div className="hidden md:flex h-full flex-col border-l border-space-30 bg-space-20/50 shrink-0">
      {tabs.length > 0 ? (
        tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => openRightPanel(t.key)}
            className="group flex flex-col items-center gap-2 px-2 py-4 hover:bg-jove-10 transition-colors"
            title={t.label}
          >
            {t.count != null && (
              <span className="text-[10px] font-semibold bg-space-30 text-space-50 group-hover:bg-jove group-hover:text-white rounded-full w-6 h-6 flex items-center justify-center transition-colors">
                {t.count}
              </span>
            )}
            <span
              className="text-[10px] font-semibold tracking-widest uppercase text-space-40 group-hover:text-jove transition-colors"
              style={{ writingMode: "vertical-rl" }}
            >
              {t.label}
            </span>
          </button>
        ))
      ) : (
        <button
          onClick={() => openRightPanel()}
          className="group flex flex-col items-center justify-center px-2 py-4 hover:bg-jove-10 transition-colors h-full"
          title="Open sidebar"
        >
          <svg className="w-4 h-4 text-space-40 group-hover:text-jove transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
          </svg>
        </button>
      )}
    </div>
  );
}

/** Inline files list — renders the two-section ArtifactsPanel in embedded mode. */
function ArtifactFilesList() {
  return <ArtifactsPanel embedded />;
}
